import OwnerLayout from "../layout/OwnerLayout";
import { useState } from "react";
import { API_BASE_URL } from "../../config/api";
import { authHeader, handleApiResponse } from "../../utils/authHeader";
import FRAUD_TYPES from "../../config/fraudTypes";

const ReportFraud = () => {
  /* BASIC DETAILS */
  const [fullName,       setFullName]       = useState("");
  const [phone,          setPhone]          = useState("");
  const [fraudType,      setFraudType]      = useState("");
  const [aadhaarNumber,  setAadhaarNumber]  = useState("");
  const [drivingLicense, setDrivingLicense] = useState("");
  const [fraudDate,      setFraudDate]      = useState("");
  const [description,    setDescription]    = useState("");

  /* FRAUD LOCATION */
  const [fraudPincode,   setFraudPincode]   = useState("");
  const [fraudCityAuto,  setFraudCityAuto]  = useState("");
  const [fraudAreaAuto,  setFraudAreaAuto]  = useState("");
  const [fraudStateAuto, setFraudStateAuto] = useState("");

  /* AADHAAR LOCATION */
  const [aadhaarPincode,   setAadhaarPincode]   = useState("");
  const [aadhaarCityAuto,  setAadhaarCityAuto]  = useState("");
  const [aadhaarAreaAuto,  setAadhaarAreaAuto]  = useState("");
  const [aadhaarStateAuto, setAadhaarStateAuto] = useState("");

  /* IMAGE */
  const [image,   setImage]   = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [loading, setLoading] = useState(false);

  /* ERRORS */
  const [errors, setErrors] = useState({});

  /* PINCODE LOOKUP */
  const fetchLocation = async (pincode, setCity, setArea, setState) => {
    if (pincode.length !== 6) return;
    try {
      const res  = await fetch(`${API_BASE_URL}/api/location/pincode/${pincode}`);
      if (!handleApiResponse(res)) return; // Handle 401 redirects (though this endpoint might not require auth)
      const data = await res.json();
      if (res.ok) {
        setCity(data.city || "");
        setArea(data.area || "");
        setState(data.state || "");
      } else {
        // Clear fields if pincode not found
        setCity(""); setArea(""); setState("");
      }
    } catch {
      // Clear fields on error
      setCity(""); setArea(""); setState("");
    }
  };

  /* VALIDATION */
  const validateForm = () => {
    const newErrors = {};

    if (!fullName.trim()) newErrors.fullName = "Full name is required";

    // ── Phone validation — supports multiple numbers separated by comma ──
    if (!phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else {
      const phones  = phone.split(",").map(p => p.trim()).filter(Boolean);
      const invalid = phones.filter(p => !/^[6-9]\d{9}$/.test(p));
      if (invalid.length > 0) {
        newErrors.phone = `Invalid: ${invalid.join(", ")} — each must be 10 digits starting with 6-9`;
      }
    }

    if (!fraudType) newErrors.fraudType = "Please select fraud type";

    if (!aadhaarNumber)                         newErrors.aadhaarNumber  = "Aadhaar number is required";
    else if (!/^\d{12}$/.test(aadhaarNumber))   newErrors.aadhaarNumber  = "Aadhaar must be 12 digits";

    if (!drivingLicense)                        newErrors.drivingLicense = "Driving licence is required";
    else if (drivingLicense.length < 8)         newErrors.drivingLicense = "Enter valid driving licence";

    if (!fraudDate) newErrors.fraudDate = "Fraud date is required";

    if (!fraudPincode || fraudPincode.length !== 6)
      newErrors.fraudPincode = "Valid fraud location pincode required";

    if (!aadhaarPincode || aadhaarPincode.length !== 6)
      newErrors.aadhaarPincode = "Valid Aadhaar location pincode required";

    if (!image && !aadhaarNumber && !drivingLicense) {
      newErrors.image = "At least one proof required: image, Aadhaar number or driving license";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* CLEAR ALL FIELDS */
  const clearAll = () => {
    setFullName(""); setPhone(""); setFraudType("");
    setAadhaarNumber(""); setDrivingLicense(""); setFraudDate(""); setDescription("");
    setFraudPincode(""); setFraudCityAuto(""); setFraudAreaAuto(""); setFraudStateAuto("");
    setAadhaarPincode(""); setAadhaarCityAuto(""); setAadhaarAreaAuto(""); setAadhaarStateAuto("");
    setImage(null); setPreview(null); setErrors({});
    setFileKey(k => k + 1);
  };

  /* SUBMIT */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    // Split multiple phone numbers by comma
    const phoneNumbers = phone.split(",").map(p => p.trim()).filter(Boolean);

    const formData = new FormData();
    formData.append("fullName",       fullName);
    formData.append("fraudType",      fraudType);
    formData.append("aadhaarNumber",  aadhaarNumber);
    formData.append("drivingLicense", drivingLicense);
    formData.append("fraudDate",      fraudDate);
    formData.append("description",    description);
    formData.append("fraudPincode",   fraudPincode);
    formData.append("fraudCity",      fraudCityAuto);
    formData.append("fraudArea",      fraudAreaAuto);
    formData.append("fraudState",     fraudStateAuto);
    formData.append("aadhaarPincode", aadhaarPincode);
    formData.append("aadhaarCity",    aadhaarCityAuto);
    formData.append("aadhaarArea",    aadhaarAreaAuto);
    formData.append("aadhaarState",   aadhaarStateAuto);
    formData.append("images",         image);

    // Send repeated fields so multipart parsing preserves the array shape.
    phoneNumbers.forEach(number => formData.append("phoneNumbers", number));
    // Also send first number as "phone" for backward compatibility
    formData.append("phone", phoneNumbers[0]);

    try {
      const res  = await fetch(`${API_BASE_URL}/api/fraud`, {
        method:  "POST",
        headers: { Authorization: authHeader().Authorization },
        body:    formData,
      });
      if (!handleApiResponse(res)) return; // Handle 401 redirects
      const data = await res.json();

      if (!res.ok) {
        const error = data.message || (data.errors ? data.errors.map(e => e.msg).join("; ") : "Failed to submit fraud report");
        alert(`Submission failed: ${error}`);
      } else {
        alert(data.message || "Fraud reported successfully");
        clearAll();
      }
    } catch (err) {
      console.error("Fraud submission failed:", err);
      alert("Fraud submission failed");
    }

    setLoading(false);
  };

  return (
    <OwnerLayout>
      <div className="dashboard-header">
        <p className="welcome-text">Report Fraud</p>
        <h2 className="company-name">Submit Fraud Details</h2>
      </div>

      <div className="owner-dashboard-wrapper">
        <div className="owner-fraud-container owner-form-container">
          <form className="fraud-form" onSubmit={handleSubmit}>
            <div className="form-grid">

              <div>
                <input placeholder="Full Name" value={fullName}
                  onChange={e => setFullName(e.target.value)} style={{ width: "100%" }} />
                {errors.fullName && <span className="error-text">{errors.fullName}</span>}
              </div>

              {/* ── PHONE — supports multiple numbers ── */}
              <div>
                <input
                  placeholder="Phone (multiple: 9876543210, 9123456780)"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  style={{ width: "100%" }}
                />
                <span style={{ fontSize: 11, color: "#a3aed0", marginTop: 3, display: "block" }}>
                  Add multiple numbers separated by comma
                </span>
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>

              <div>
                <select value={fraudType} onChange={e => setFraudType(e.target.value)} style={{ width: "100%" }}>
                  <option value="">Select Fraud Type</option>
                  {FRAUD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                {errors.fraudType && <span className="error-text">{errors.fraudType}</span>}
              </div>

              <div>
                <input placeholder="Aadhaar Number" value={aadhaarNumber}
                  onChange={e => setAadhaarNumber(e.target.value)} style={{ width: "100%" }} />
                {errors.aadhaarNumber && <span className="error-text">{errors.aadhaarNumber}</span>}
              </div>

              <div>
                <input placeholder="Driving Licence Number" value={drivingLicense}
                  onChange={e => setDrivingLicense(e.target.value)} style={{ width: "100%" }} />
                {errors.drivingLicense && <span className="error-text">{errors.drivingLicense}</span>}
              </div>

              <div>
                <input type="date" value={fraudDate}
                  onChange={e => setFraudDate(e.target.value)} style={{ width: "100%" }} />
                {errors.fraudDate && <span className="error-text">{errors.fraudDate}</span>}
              </div>

              <div>
                <input placeholder="Fraud Location Pincode" value={fraudPincode}
                  onChange={e => {
                    setFraudPincode(e.target.value);
                    if (e.target.value.length === 6) {
                      setFraudCityAuto(""); setFraudAreaAuto(""); setFraudStateAuto("");
                      fetchLocation(e.target.value, setFraudCityAuto, setFraudAreaAuto, setFraudStateAuto);
                    } else {
                      setFraudCityAuto(""); setFraudAreaAuto(""); setFraudStateAuto("");
                    }
                  }} style={{ width: "100%" }} />
                {errors.fraudPincode && <span className="error-text">{errors.fraudPincode}</span>}
              </div>

              <input placeholder="Fraud City"  value={fraudCityAuto}  readOnly />
              <input placeholder="Fraud Area"  value={fraudAreaAuto}  readOnly />
              <input placeholder="Fraud State" value={fraudStateAuto} readOnly />

              <div>
                <input placeholder="Aadhaar Location Pincode" value={aadhaarPincode}
                  onChange={e => {
                    setAadhaarPincode(e.target.value);
                    if (e.target.value.length === 6) {
                      setAadhaarCityAuto(""); setAadhaarAreaAuto(""); setAadhaarStateAuto("");
                      fetchLocation(e.target.value, setAadhaarCityAuto, setAadhaarAreaAuto, setAadhaarStateAuto);
                    } else {
                      setAadhaarCityAuto(""); setAadhaarAreaAuto(""); setAadhaarStateAuto("");
                    }
                  }} style={{ width: "100%" }} />
                {errors.aadhaarPincode && <span className="error-text">{errors.aadhaarPincode}</span>}
              </div>

              <input placeholder="Aadhaar City"  value={aadhaarCityAuto}  readOnly />
              <input placeholder="Aadhaar Area"  value={aadhaarAreaAuto}  readOnly />
              <input placeholder="Aadhaar State" value={aadhaarStateAuto} readOnly />

            </div>

            <textarea placeholder="Fraud Description" rows="4"
              value={description} onChange={e => setDescription(e.target.value)} />

            <div className="file-upload">
              <label>Upload Fraud Proof - Person Photo</label>
              <input key={fileKey} type="file" accept="image/*"
                onChange={e => {
                  const f = e.target.files[0];
                  setImage(f);
                  if (f) setPreview(URL.createObjectURL(f));
                }} />
              {errors.image && <span className="error-text">{errors.image}</span>}
              {preview && (
                <div className="image-preview">
                  <img src={preview} alt="Preview" style={{ width: "120px", height: "auto" }} />
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={clearAll}>Clear</button>
              <button type="submit" className="save-btn" disabled={loading}>
                {loading ? "Submitting..." : "Submit Fraud"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </OwnerLayout>
  );
};

export default ReportFraud;